import { EventAction, EventActionType } from '../../actions';
import { EventEntry, EventStatus, EventsState } from '../../state';
import produce, { Draft } from 'immer';
import { serializeErrors } from '../serialize-errors';

const initialState: EventsState = Object.freeze({});

const emptyEvent: EventEntry['event'] = Object.freeze({
  categoryIds: [],
  description: '',
  id: '',
  locationId: null,
  name: '',
  occurrences: [],
  pageId: null,
  pageTitle: null,
  placeId: null,
  placeName: null,
  posterImages: [],
  recurrenceRules: null,
  tagIds: [],
  tags: '',
});

const emptyEntry: EventEntry = Object.freeze({
  errors: null,
  event: emptyEvent,
  status: null,
});

const getEvent = <A extends { eventKey: string }>(
  state: Draft<EventsState>,
  action: A,
): Draft<EventEntry> => {
  const entry = state[action.eventKey];
  if (entry) {
    return entry;
  } else {
    throw new Error('Event not in state.');
  }
};

const setEventStatus = <A extends { errors?: Error[]; eventKey: string }>(
  state: Draft<EventsState>,
  action: A,
  status: EventStatus,
): void => {
  const entry = getEvent(state, action);
  entry.status = status;
  if (action.errors) {
    entry.errors = serializeErrors(action.errors);
  } else {
    entry.errors = null;
  }
};

export const eventsReducer = (
  state: EventsState = initialState,
  action: EventAction,
): EventsState =>
  produce(state, (draft): void => {
    switch (action.type) {
      case EventActionType.CreateEventError:
        return setEventStatus(draft, action, EventStatus.CreateError);

      case EventActionType.CreateEventRequest:
        return setEventStatus(draft, action, EventStatus.CreateRequested);

      case EventActionType.CreateEventSuccess:
        return setEventStatus(draft, action, EventStatus.CreateSuccess);

      case EventActionType.DeleteEventError:
        return setEventStatus(draft, action, EventStatus.DeleteError);

      case EventActionType.DeleteEventRequest:
        return setEventStatus(draft, action, EventStatus.DeleteRequested);

      case EventActionType.DeleteEventSuccess:
        return setEventStatus(draft, action, EventStatus.DeleteSuccess);

      case EventActionType.LoadEvent:
        draft[action.eventKey] = {
          ...emptyEntry,
          event: {
            ...emptyEvent,
            ...action.event,
          },
        };
        for (const occ of draft[action.eventKey].event.occurrences) {
          occ.key = Math.random()
            .toString(16)
            .slice(2);
        }
        return;

      case EventActionType.LoadEventError:
        return setEventStatus(draft, action, EventStatus.LoadError);

      case EventActionType.LoadEventRequest:
        draft[action.eventKey] = { ...emptyEntry };
        return setEventStatus(draft, action, EventStatus.LoadRequested);

      case EventActionType.LoadEventSuccess:
        draft[action.eventKey].event = {
          ...emptyEvent,
          ...action.event,
        };
        for (const occ of draft[action.eventKey].event.occurrences) {
          occ.key = Math.random()
            .toString(16)
            .slice(2);
        }
        return setEventStatus(draft, action, EventStatus.LoadSuccess);

      case EventActionType.SetEventCategoryIds:
        getEvent(draft, action).event.categoryIds = action.categoryIds;
        return;

      case EventActionType.SetEventDescription:
        getEvent(draft, action).event.description = action.description;
        return;

      case EventActionType.SetEventName:
        getEvent(draft, action).event.name = action.name;
        return;

      case EventActionType.SetEventPageId:
        getEvent(draft, action).event.pageId = action.pageId;
        return;

      case EventActionType.SetEventPageTitle:
        getEvent(draft, action).event.pageTitle = action.title;
        return;

      case EventActionType.SetEventPlaceId:
        getEvent(draft, action).event.placeId = action.placeId;
        return;

      case EventActionType.SetEventPlaceName:
        getEvent(draft, action).event.placeName = action.name;
        return;

      case EventActionType.SetEventPosterImages:
        getEvent(draft, action).event.posterImages = action.posterImages;
        return;

      case EventActionType.SetEventRecurrenceRules:
        getEvent(draft, action).event.recurrenceRules = action.recurrenceRules;
        return;

      case EventActionType.SetEventStatus:
        getEvent(draft, action).status = action.status;
        return;

      case EventActionType.SetEventTagIds:
        getEvent(draft, action).event.tagIds = action.tagIds;
        return;

      case EventActionType.SetEventTags:
        getEvent(draft, action).event.tags = action.tags;
        return;

      case EventActionType.AddOccurrence: {
        getEvent(draft, action).event.occurrences.splice(0, 0, {
          key: Math.random()
            .toString(16)
            .slice(2),
          startsAt: action.startsAt,
          duration: action.duration,
        });
        return;
      }

      case EventActionType.AddManyOccurrences: {
        getEvent(draft, action).event.occurrences.push(
          ...action.occurrences.map(o => ({
            ...o,
            key: Math.random()
              .toString(16)
              .slice(2),
            startsAt: o.startsAt.toISOString(),
          })),
        );
        return;
      }

      case EventActionType.RemoveOccurrence: {
        getEvent(draft, action).event.occurrences.splice(action.index, 1);
        return;
      }

      case EventActionType.RemoveAllOccurrences: {
        getEvent(draft, action).event.occurrences = [];
        return;
      }

      case EventActionType.SetOccurrenceDuration: {
        getEvent(draft, action).event.occurrences[
          action.occurrenceIndex
        ].duration = action.duration;
        return;
      }

      case EventActionType.SetOccurrenceStartsAt: {
        getEvent(draft, action).event.occurrences[
          action.occurrenceIndex
        ].startsAt = action.startsAt;
        return;
      }

      // TODO: this may need to be handled with an epic to add a delay between
      // when the unload is requested and when it is accomplished to avoid
      // disappearing UI state.
      case EventActionType.UnloadEvent:
        delete draft[action.eventKey];
        return;

      case EventActionType.UpdateEventError:
        return setEventStatus(draft, action, EventStatus.UpdateError);

      case EventActionType.UpdateEventRequest:
        return setEventStatus(draft, action, EventStatus.UpdateRequested);

      case EventActionType.UpdateEventSuccess:
        return setEventStatus(draft, action, EventStatus.UpdateSuccess);
    }
  });
